import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Category } from "../../types";
import { useCategoriesStore } from "../../store/categoriesStore";
import { useProductStore } from "../../store/productStore";

interface HamburgerMenuProps {
  onClose: () => void;
  isMobile: boolean;
}

function HamburgerMenu({ onClose, isMobile }: HamburgerMenuProps) {
  const { categories, fetchCategories, activeCategories, setActiveCategories } = useCategoriesStore();
  const { setFilter, resetFilters } = useProductStore();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleMouseEnter = (category: Category, level: number) => {
    setActiveCategories([...activeCategories.slice(0, level), category]);
  };

  const handleClick = (category: Category) => {
    setActiveCategories([category]);
    resetFilters(); // Сбрасываем прочие фильтры
    setFilter("category_id", category.id);
    navigate(`/catalog?category_id=${category.id}`);
    onClose();
  };

  const handleMouseLeave = () => {
    setActiveCategories([]);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Определяем inline‑стили для колонок и списка
  const mainColumnStyle: React.CSSProperties = {
    width: 250,
    maxHeight: "calc(100vh - 100px)",
    overflowY: "auto",
    marginRight: 16,
    display: "flex",
    flexDirection: "column",
  };

  const subColumnStyle: React.CSSProperties = {
    width: 270,
    maxHeight: "calc(100vh - 100px)",
    overflowY: "auto",
    marginRight: 16,
    display: "flex",
    flexDirection: "column",
  };

  const ulStyle: React.CSSProperties = {
    listStyle: "none",
    margin: 0,
    padding: 0,
  };

  const liStyle = (isActive: boolean): React.CSSProperties => ({
    cursor: "pointer",
    padding: "5px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "14px",
    fontWeight: isActive ? "bold" : "normal",
  });

  return (
    <div
      ref={menuRef}
      className={`fixed ${isMobile ? "bottom-[57px] left-0 right-0 h-3/4" : "top-16 left-0 right-0"
        } bg-white shadow-md z-40 overflow-y-auto `}
      onMouseLeave={!isMobile ? handleMouseLeave : undefined}
    >
      <div className="flex container mx-auto py-5 md:px-4">
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "start",
            alignItems: "flex-start",
          }}
        >
          {/* Первая колонка — корневые категории */}
          <div style={mainColumnStyle}>
            <ul style={ulStyle}>
              {categories.map((category) => {
                const isActive = activeCategories[0]?.id === category.id;
                return (
                  <li
                    key={category.id}
                    style={liStyle(isActive)}
                    onMouseEnter={() => handleMouseEnter(category, 0)}
                    onClick={() => handleClick(category)}
                  >
                    {category.name}
                    {category.children && category.children.length > 0 && (
                      <div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={isActive ? 2.5 : 1.5}
                          stroke={isActive ? "black" : "gray"}
                          style={{ width: 16, height: 16 }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8.25 4.5l7.5 7.5-7.5 7.5"
                          />
                        </svg>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Остальные колонки — подкатегории */}
          {activeCategories.map((activeCategory, index) => {
            const children = activeCategory.children || [];
            if (children.length === 0) return null;
            return (
              <div key={activeCategory.id} style={subColumnStyle}>
                <ul style={ulStyle}>
                  {children.map((child) => {
                    const isActive = activeCategories[index + 1]?.id === child.id;
                    return (
                      <li
                        key={child.id}
                        style={liStyle(isActive)}
                        onMouseEnter={() => handleMouseEnter(child, index + 1)}
                        onClick={() => handleClick(child)}
                      >
                        {child.name}
                        {child.children && child.children.length > 0 && (
                          <div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={isActive ? 2.5 : 1.5}
                              stroke={isActive ? "black" : "gray"}
                              style={{ width: 16, height: 16 }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8.25 4.5l7.5 7.5-7.5 7.5"
                              />
                            </svg>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default HamburgerMenu;
